import { useState } from "react";
import { Target, AlertOctagon, Compass, Activity } from "lucide-react";
import { STATS, TESTIMONIALS, ANALYSIS_EXAMPLES, RISK_DISCLOSURE } from "./data";

export function StatsBar() {
  return (
    <section
      data-testid="stats-section"
      className="relative border-y border-white/[0.06] bg-[#070707]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`stat-card-${i}`}
              className="md:px-6 md:border-r md:border-white/[0.06] md:last:border-r-0"
            >
              <div className="flex items-baseline gap-2">
                <span className="font-display font-bold text-5xl md:text-6xl text-white tracking-tighter">
                  {s.value}
                </span>
                <span className="h-2 w-2 rounded-full bg-[#00FF66] mb-2" />
              </div>
              <p className="mt-2 text-white font-display font-semibold text-base">
                {s.label}
              </p>
              <p className="mt-1 text-neutral-500 text-sm">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Dawniej "Ostatnie wyniki" (ResultsMarquee) — teraz "Jak analizujemy rynek".
// Pokazuje strukturę analityczną zamiast prezentować procentowe stopy zwrotu.
export function AnalysisProcess() {
  return (
    <section
      id="analiza"
      data-testid="analysis-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <SectionLabel>Jak analizujemy rynek</SectionLabel>
          <h2
            data-testid="analysis-headline"
            className="mt-4 font-display font-bold tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl leading-[0.98]"
          >
            Konkretny proces,<br />
            <span className="text-[#00FF66]">nie przypadek.</span>
          </h2>
          <p className="mt-5 text-neutral-400 text-lg leading-relaxed">
            Każdą obserwację opieramy o kontekst wyższego interwału, jasno określone
            poziomy cenowe i warunki unieważnienia scenariusza. Poniżej przykładowy
            schemat analizy — służy wyłącznie celom poglądowym i edukacyjnym.
          </p>
        </div>

        {/* Analytical pillars */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              icon: Compass,
              title: "Kontekst rynkowy",
              desc: "Struktura wyższego interwału oraz dominujący scenariusz.",
            },
            {
              icon: Target,
              title: "Kluczowe poziomy",
              desc: "Strefy popytu, podaży i poziomy, przy których spodziewamy się reakcji.",
            },
            {
              icon: AlertOctagon,
              title: "Warunek unieważnienia",
              desc: "Jasno zdefiniowany poziom, który unieważnia obserwowany scenariusz.",
            },
            {
              icon: Activity,
              title: "Zarządzanie ryzykiem",
              desc: "Rozmiar pozycji dobrany do akceptowalnej straty na transakcję.",
            },
          ].map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`analysis-pillar-${i}`}
                className="hs-card rounded-2xl p-6"
              >
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-white/[0.04] text-[#00FF66] border border-white/[0.06]">
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <h3 className="mt-5 font-display font-semibold tracking-tight text-white text-lg">
                  {p.title}
                </h3>
                <p className="mt-2 text-neutral-400 text-sm leading-relaxed">
                  {p.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Example analysis cards */}
        <h3 className="mt-16 text-[11px] uppercase tracking-[0.22em] font-bold text-neutral-500">
          Przykładowe schematy analizy
        </h3>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {ANALYSIS_EXAMPLES.map((a, i) => (
            <article
              key={a.pair}
              data-testid={`analysis-example-${i}`}
              className="hs-card rounded-2xl p-6"
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-display font-semibold tracking-tight">
                  {a.pair}
                </span>
                <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400 px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.06]">
                  {a.status}
                </span>
              </div>
              <dl className="mt-4 grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                    Obserwowany scenariusz
                  </dt>
                  <dd className="mt-1 text-neutral-200">{a.scenario}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                    Kluczowy poziom
                  </dt>
                  <dd className="mt-1 text-neutral-200">{a.level}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                    Warunek unieważnienia
                  </dt>
                  <dd className="mt-1 text-red-300/90">{a.invalidation}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>

        <p
          data-testid="analysis-disclaimer"
          className="mt-10 text-xs md:text-sm text-neutral-500 leading-relaxed max-w-3xl"
        >
          {RISK_DISCLOSURE}
        </p>
      </div>
    </section>
  );
}

export function Testimonials() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section
      id="opinie"
      data-testid="testimonials-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <SectionLabel>Opinie społeczności</SectionLabel>
          <h2
            data-testid="testimonials-headline"
            className="mt-4 font-display font-bold tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl leading-[0.98]"
          >
            Opinie <span className="text-[#00FF66]">społeczności.</span>
          </h2>
          <p className="mt-5 text-neutral-400 text-lg leading-relaxed">
            Fragmenty rozmów z członkami grupy, udostępnione za zgodą. Opinie dotyczą
            indywidualnych doświadczeń i nie stanowią obietnicy podobnych rezultatów.
          </p>
        </div>

        <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              data-testid={`testimonial-card-${i}`}
              className="mb-5 break-inside-avoid hs-card rounded-2xl overflow-hidden group cursor-pointer"
              onClick={() => setLightbox(t.image)}
            >
              <div className="relative bg-black">
                <img
                  src={t.image}
                  alt={t.caption}
                  loading="lazy"
                  className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
                />
              </div>
              <figcaption className="flex items-center gap-2.5 px-5 py-4 border-t border-white/[0.06]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00FF66] shrink-0" />
                <span className="text-neutral-300 text-sm font-medium">
                  {t.caption}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          data-testid="testimonial-lightbox"
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
            data-testid="lightbox-close"
            aria-label="Zamknij"
          >
            ✕
          </button>
          <img
            src={lightbox}
            alt=""
            className="max-h-[92vh] max-w-full w-auto h-auto rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}

export function SectionLabel({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#00FF66]">
      <span className="h-1 w-6 bg-[#00FF66] rounded-full" />
      {children}
    </span>
  );
}
